import React, { useState, useEffect, useRef, useContext } from 'react'
import PropTypes from 'prop-types'
import { useSelector } from 'react-redux'
import { GlobalBaseStyle, CenterContent, Question, Hint, Instruction } from '../../assets/styles/BaseStyle';
import { Group } from '../GroupRadioButtonImage/GroupRadioButtonImageStyles'
import { extract_text } from '../../helpers';
import { LocaleContext } from '../../context/LocalContext';

import { loadImage } from '../../helpers/image_loader';
import _get from 'lodash.get';
import {
  ModalOverlay,
  ModalContent,
  ModalTitle,
  ModalInput,
  ModalHelpText,
  ModalButtonContainer,
  ModalButton,
  Tile,
  TileImageContainer,
  TileImage,
  TileLabel,
  TileCheckmark,
  GridContainer,
  OtherOptionContainer,
  OtherOptionButton,
  OtherOptionRadio,
  OtherOptionText,
  MainImageContainer,
  MainImage
} from './GroupImagePickStyles';


let currentTime = Date.now()

// Simple modal implementation
const Modal = ({ open, onClose, children }) => {
  if (!open) return null;
  return (
    <ModalOverlay onClick={onClose}>
      <ModalContent onClick={e => e.stopPropagation()}>
        {children}
      </ModalContent>
    </ModalOverlay>
  );
};

// Tile with large image and small label area
const TileComponent = ({ selected, onClick, imageSrc, label, alt, isPortrait, columns, disabled }) => (
  <Tile
    selected={selected}
    onClick={onClick}
    isPortrait={isPortrait}
    columns={columns}
    style={disabled ? { pointerEvents: 'none', cursor: 'not-allowed' } : {}}
    tabIndex={disabled ? -1 : 0}
    aria-disabled={disabled}
    label={label}
  >
    <TileImageContainer>
      <TileImage
        src={loadImage(imageSrc)}
        alt={alt}
        selected={selected}
      />
    </TileImageContainer>
    {label &&
    <TileLabel selected={selected}>
      {label}
    </TileLabel>
    }
    {selected && (
      <TileCheckmark aria-label="Selected">
        ✓
      </TileCheckmark>
    )}
  </Tile>
);

function getGridColumns(optionCount) {
  if (optionCount === 4) return 2;
  if (optionCount === 6) return 3;
  // fallback: 1 for 1, 2 for 2, 3 for 3, 3 for 5, 4 for 8, etc.
  if (optionCount % 3 === 0) return 3;
  if (optionCount % 2 === 0) return 2;
  return Math.min(optionCount, 3);
}

const GroupImagePick = ({
  observation, title, options, center, other_option,
  handPointer, value, add_to_state, defaultValue, image,
  onChangeHandler, sub_title, instruction , help= false
}) => {
  const translation = useContext(LocaleContext);
  const { client } = useSelector((state) => state.config);
  const isRtl = client?.isRtl || false;
 // const isRtl = false;
  const componentRef = useRef(true);

  const [selected, setSelected] = useState(_get(value, 'value', null));
  const [label, setLabel] = useState(null);
  const [other_option_value, setOtherOptionValue] = useState(_get(value, `other_option_value.${_get(other_option, 'observation')}`));
  const [sequence, setSequence] = useState(_get(value, 'sequence', []));
  const [timeSequence, setTimeSequence] = useState(_get(value, 'timeSequence', []));
  const [showOtherModal, setShowOtherModal] = useState(false);
  const [tempOtherValue, setTempOtherValue] = useState('');

  useEffect(() => {
    setSelected(_get(value, 'value', defaultValue || null));
    setLabel(_get(value, 'label', null));
    setSequence(_get(value, 'sequence', []));
    setTimeSequence(_get(value, 'timeSequence', []));
    setOtherOptionValue(_get(value, `other_option_value.${_get(other_option, 'observation')}`))
  }, [observation]);

  useEffect(() => {
    if (!componentRef.current) {
      if (typeof onChangeHandler === 'function') {
        if (selected === null) {
          // Handle deselection - call onChangeHandler with null to disable next button
          onChangeHandler({
            value: null,
            other_option_value: null,
            sequence,
            timeSequence,
            label: null,
            itsDone: false
          }, {})
        } else {
          // Handle selection
          const other_option_invalid = !other_option_value || other_option_value.length < 1;
          const other_option_selected = selected === _get(other_option, 'value', 7);

          if (other_option_invalid && other_option_selected) {
            onChangeHandler(null)
          } else {
            const state = {};
            if (add_to_state) {
              state[typeof add_to_state === 'string' ? add_to_state : observation] = selected;
            }

            // If "Other" is selected, label should be "Other - {other_option_value}"
            let responseLabel = label;
            if (other_option_selected) {
              responseLabel = `Other - ${other_option_value}`;
            }

            onChangeHandler({
              value: selected,
              other_option_value: other_option_selected ? { [other_option.observation]: other_option_value } : null,
              sequence,
              timeSequence,
              label: responseLabel,
              itsDone: selected !== null
            }, state)
          }
        }
      }
    } else {
      componentRef.current = false
    }
  }, [selected, other_option_value]);

  // If help is true, disable all click actions
  const handleSelect = (value, label) => {
    if (help) return;

    const time = Date.now() - currentTime
    // If the clicked value is already selected, deselect it
    if (selected === value) {
      setSelected(null);
      setLabel(null);
      setSequence(sequence => {
        // Deselect: push negative of value
        sequence.push(-value);
        return sequence;
      });
      setTimeSequence([...timeSequence, time])
      // Optionally, you may want to clear other_option_value if the deselected option is the other_option
      if (value === _get(other_option, 'value', 'other_option')) {
        setOtherOptionValue(undefined);
      }
      // Do not update sequence/timeSequence on deselect
      return;
    }

    // Check if this is the "Other" option
    if (value === _get(other_option, 'value', 7)) {
      setTempOtherValue(other_option_value || '');
      setShowOtherModal(true);
      return;
    }

    setSelected(value);
    setLabel(label);
    setSequence(sequence => {
      if (value !== sequence[sequence.length - 1]) sequence.push(value);
      return sequence
    });
    setTimeSequence([...timeSequence, time])
  };

  const handleOtherSave = () => {
    if (tempOtherValue.trim()) {
      const O_time = Date.now() - currentTime
      setSelected(_get(other_option, 'value', 7));
      setLabel(`Other - ${tempOtherValue.trim()}`); // <-- Set label as "Other - {typed value}"
      setOtherOptionValue(tempOtherValue.trim());
      setSequence(sequence => {
        const otherValue = _get(other_option, 'value', 7);
        if (sequence.length === 0 || otherValue !== sequence[sequence.length - 1]) {
          return [...sequence, otherValue];
        }
        return sequence;
      });

      setTimeSequence(timeSequence => {
        const otherValue = _get(other_option, 'value', 7);
        if (timeSequence.length === 0 || otherValue !== sequence?.[sequence.length - 1]) {
          return [...timeSequence, O_time];
        }
        return timeSequence;
      });
    }
    setShowOtherModal(false);
  };

  const handleOtherCancel = () => {
    setTempOtherValue('');
    setShowOtherModal(false);
  };

  const generateOptions = () => {
    const defaultOptions = [...options];
    // Do not add other_option to the tile options; handle it separately in the UI.
    const isPortrait = observation === "fv_crop_pattern" ? true : false;
    const columns = isPortrait ? 1 : getGridColumns(defaultOptions.length);
    return (
      <GridContainer
        isPortrait={isPortrait}
        columns={columns}
      >
        {defaultOptions.map((item, index) => {
          const is_selected = selected === item.value;
          return (
            <TileComponent
              key={item.value || index}
              selected={is_selected}
              onClick={() => handleSelect(item.value, item.label || _get(translation, `${observation}.option${index + 1}`))}
              imageSrc={item?.image}
              label={observation !== "fv_farm_challenge" ? extract_text(_get(translation, `${observation}.option${index + 1}`) || item.label) : null}
              alt={extract_text(item.label || _get(translation, `${observation}.option${index + 1}`))}
              isPortrait={isPortrait}
              columns={columns}
              disabled={help}
            />
          );
        })}
      </GridContainer>
    );
  };


  return (
    <>
      <GlobalBaseStyle />
      <CenterContent center={center}>
        {image && (
          <MainImageContainer>
            <MainImage
              src={loadImage(image)}
              alt={extract_text(title) || _get(translation, `${observation}.question`)}
            />
          </MainImageContainer>
        )}
        {
          <>
            <Question alignment={title.alignment || 'center'}>
              {extract_text(title) || _get(translation, `${observation}.question`)}
            </Question>
            <Instruction
              visible={instruction}
              alignment={_get(instruction, 'alignment', 'left')}
            >
              {extract_text(instruction) || _get(translation, `${observation}.instruction`, '')}
            </Instruction>
            <Hint
              visible={sub_title}
              render_as={_get(sub_title, 'as', 'hint')}
              alignment={_get(sub_title, 'alignment', 'left')}
            >
              {extract_text(sub_title) || _get(translation, `${observation}.hint`, _get(translation, `${observation}.subtitle`))}
            </Hint>
          </>

        }
        <Group style={{ width: '100%', justifyContent: 'center' }}>
          {generateOptions()}

          {/* Other Option Button */}
          {other_option && (
            <OtherOptionContainer>
              <OtherOptionButton
                selected={selected === _get(other_option, 'value', 7)}
                onClick={() => handleSelect(_get(other_option, 'value', 7), other_option_value ? `Other - ${other_option_value}` : 'Other')}
                style={help ? { pointerEvents: 'none', cursor: 'not-allowed' } : {}}
                tabIndex={help ? -1 : 0}
                aria-disabled={help}
              >
                <OtherOptionRadio selected={selected === _get(other_option, 'value', 7)}>
                  {selected === _get(other_option, 'value', 7) && '✓'}
                </OtherOptionRadio>
                <OtherOptionText selected={selected === _get(other_option, 'value', 7)}>
                  {other_option_value && selected === _get(other_option, 'value', 7)
                    ? `${_get(translation, `${observation}.other_option.label`)} - ${other_option_value}`
                    : _get(translation, `${observation}.other_option.label`)}
                </OtherOptionText>
              </OtherOptionButton>
            </OtherOptionContainer>
          )}
        </Group>

        {/* Other Option Modal */}
        <Modal open={showOtherModal} onClose={handleOtherCancel} isRtl={isRtl}>
          <div style={{ minWidth: 300 }}>
            <ModalTitle isRtl={isRtl}>
              {_get(translation, `${observation}.other_option.modal_content.modal_title`) || 'Specify Other Option'}
            </ModalTitle>
            <div style={{ marginBottom: 20 }}>
              <ModalInput
                type={_get(other_option, 'meta.type', 'text')}
                value={tempOtherValue}
                onChange={(e) => setTempOtherValue(e.target.value)}
                placeholder={_get(translation, `${observation}.other_option.modal_content.modal_placeholder`)|| _get(other_option, 'meta.placeholder', 'Enter your custom option...')}
                maxLength={_get(other_option, 'meta.maxLength', 50)}
                minLength={_get(other_option, 'meta.minLength', 0)}
                required={_get(other_option, 'meta.required', false)}
                pattern={_get(other_option, 'meta.pattern', undefined)}
                disabled={_get(other_option, 'meta.disabled', false)}
                readOnly={_get(other_option, 'meta.readOnly', false)}
                autoComplete={_get(other_option, 'meta.autoComplete', 'off')}
                autoFocus
                isRtl={isRtl}
                style={_get(other_option, 'meta.style', {})}
              />
              {_get(other_option, 'meta.helpText') && (
                <ModalHelpText isRtl={isRtl} style={_get(other_option, 'meta.helpTextStyle', {})}>
                  {_get(translation, `${observation}.other_option.modal_content.modal_help_text`)|| _get(other_option, 'meta.helpText')}
                </ModalHelpText>
              )}
            </div>
            <ModalButtonContainer isRtl={isRtl}>
              <ModalButton
                variant="cancel"
                onClick={handleOtherCancel}
                isRtl={isRtl}
              >
                {_get(translation, `${observation}.other_option.modal_content.modal_button_cancel`) || "Cancel"}
              </ModalButton>
              <ModalButton
                onClick={handleOtherSave}
                disabled={!tempOtherValue.trim()}
                isRtl={isRtl}
              >
                {_get(translation, `${observation}.other_option.modal_content.modal_button_save`) || "Save"}
              </ModalButton>
            </ModalButtonContainer>
          </div>
        </Modal>
      </CenterContent>
    </>
  );
};

GroupImagePick.defaultProps = {
  options: [],
  value: null,
  handPointer: false,
  sub_title: '',
  title: ''
};

GroupImagePick.propTypes = {
  value: PropTypes.object,
  handPointer: PropTypes.oneOfType([
    PropTypes.bool,
    PropTypes.string,
    PropTypes.number
  ]),
  options: PropTypes.array.isRequired,
  onChangeHandler: PropTypes.func
};

export default GroupImagePick;